import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import type { ClassAttributes } from 'react';

import { ButtonLink } from '@yoweb/ui/components/buttons';
import { Box } from '@yoweb/ui/components/Box';
import { LegalHero } from './LegalHero';
import {
  H2Legal,
  H3Legal,
  H3LegalSpaced,
  H4Legal,
  Ul,
  Ol,
  Th,
  Td,
  TdRegistration,
  Table,
  Bold,
  Italic,
  TextLegalese,
  HrLegal,
} from '@yoweb/ui/components/markdown';

type LegalMarkdownRendererProps = {
  markdown: string;
  listTopLevelAlpha?: boolean;
  embedded?: boolean;
  frontmatter: {
    [key: string]: string;
  };
};

const A = (props: ClassAttributes<HTMLAnchorElement>) => (
  <ButtonLink size="md" target="_blank" {...props} />
);

// Note: embedded links cannot be opened via target="_blank" based on the current webview implementation see: WEB-2210
const EmbedA = (props: ClassAttributes<HTMLAnchorElement>) => <ButtonLink size="md" {...props} />;

const LegalMarkdownRenderer = ({
  markdown,
  frontmatter,
  listTopLevelAlpha,
  embedded,
}: LegalMarkdownRendererProps) => (
  <>
    <LegalHero company={frontmatter.company} title={frontmatter.title} date={frontmatter.date} />
    <Box pt="normal2">
      <ReactMarkdown
        components={{
          p: (props) => <TextLegalese {...props} />,
          h2: (props) => <H2Legal {...props} />,
          h3: (props) => <H3Legal {...props} />,
          h4: (props) => <H4Legal {...props} />,
          ul: (props) => <Ul {...props} />,
          ol: (props) => (listTopLevelAlpha ? <Ol {...props} /> : <Ol isNumeric {...props} />),
          li: (props) => <TextLegalese as="li" {...props} />,
          a: embedded ? EmbedA : A,
          strong: (props) => <Bold {...props} />,
          em: (props) => <Italic {...props} />,
          hr: (props) => <HrLegal {...props} />,
          // @ts-ignore Proptype mismatch, fix later
          th: (props) => <Th {...props} />,
          // @ts-ignore Proptype mismatch, fix later
          td: (props) => <Td {...props} />,
          table: (props) => <Table {...props} />,
        }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  </>
);

const TravelRegistrationMarkdownRenderer = ({
  markdown,
  frontmatter,
  embedded,
}: LegalMarkdownRendererProps) => (
  <>
    <LegalHero company={frontmatter.company} title={frontmatter.title} date={frontmatter.date} />
    <Box pt="normal2">
      <ReactMarkdown
        components={{
          h3: (props) => <H3Legal {...props} />,
          p: (props) => <TextLegalese {...props} />,
          a: embedded ? EmbedA : A,
          // @ts-ignore Proptype mismatch, fix later
          td: (props) => <TdRegistration {...props} />,
          table: (props) => <Table {...props} />,
        }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  </>
);

const TravelConditionsMarkdownRenderer = ({
  markdown,
  frontmatter,
  embedded,
}: LegalMarkdownRendererProps) => (
  <>
    <LegalHero company={frontmatter.company} title={frontmatter.title} date={frontmatter.date} />
    <Box pt="normal2">
      <ReactMarkdown
        components={{
          h3: (props) => <H3LegalSpaced {...props} />,
          p: (props) => <TextLegalese {...props} />,
          a: embedded ? EmbedA : A,
          // @ts-ignore Proptype mismatch, fix later
          td: (props) => <TdRegistration {...props} />,
          table: (props) => <Table {...props} />,
        }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  </>
);

export {
  LegalMarkdownRenderer,
  TravelRegistrationMarkdownRenderer,
  TravelConditionsMarkdownRenderer,
};

export default LegalMarkdownRenderer;
